/**
 * Shared cursor-to-symbol resolution used by hover, definition,
 * references, call-hierarchy. Falls back from qualified-name resolution
 * (for references like `Orders.CreateOrder`) to span containment (for
 * declaration sites where the source only has the simple name).
 */

import type { Workspace } from "./workspace.js";
import type { SymbolTable, SymbolEntry } from "./symbol-table.js";
import type { CursorContext } from "./cursor-context.js";

export function resolveSymbolAtCursor(
  workspace: Workspace,
  symbols: SymbolTable,
  cursor: CursorContext,
  uri: string,
  line: number,
  character: number
): SymbolEntry | undefined {
  // Aspect values are string literals, not qualified-name tokens, so the
  // qualifiedNameAt probe below never sees them. Probe the aspect index
  // by offset first so hover / find-references / rename work on a string
  // label value at any occurrence.
  const aspectFile = workspace.getFile(uri);
  if (aspectFile) {
    const offset = offsetOf(aspectFile.text, line, character);
    // Re-entry labels (`as` / `go`, PROCESS.md §7) are bare identifiers that DO
    // form a qualifiedNameAt token, but they resolve process-locally, not to a
    // workspace symbol — probe them (and aspects) by offset first so the
    // generic name lookup below never mis-resolves them.
    const reentry = symbols.reentryLabelAt(uri, offset);
    if (reentry) return reentry;
    const aspect = symbols.aspectAt(uri, offset);
    if (aspect) return aspect;
  }

  const hit = cursor.qualifiedNameAt(uri, line, character);
  if (!hit) return undefined;

  // The referrer uri makes twin names across spaces resolve to the SAME
  // space's declaration (v0.11 — nearest-first space chain).
  const sym = symbols.resolve(hit.text, uri);
  if (sym) return sym;

  // Declaration-site fallback: the cursor is on an identifier that
  // doesn't resolve as a qualified name (e.g. `interface CreateOrder`
  // inside module Orders, where the source only spells the simple name).
  // Find the smallest symbol whose defining span contains this cursor
  // and whose simple name matches the identifier under the cursor.
  const file = workspace.getFile(uri);
  if (!file) return undefined;
  const offset = offsetOf(file.text, line, character);
  const candidate = symbols.resolveAt(uri, offset);
  if (!candidate) return undefined;
  if (candidate.name === hit.text || candidate.qualifiedName === hit.text) {
    return candidate;
  }
  return undefined;
}

export function offsetOf(text: string, line: number, character: number): number {
  let currentLine = 0;
  let lineStart = 0;
  for (let i = 0; i < text.length; i++) {
    if (currentLine === line) break;
    if (text[i] === "\n") {
      currentLine++;
      lineStart = i + 1;
    }
  }
  return lineStart + character;
}
